import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { CalendarDays, ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Reveal } from "./Reveal";
import { createLead } from "@/lib/api";
import { getAttribution } from "@/lib/utm";
import { trackLeadCreated } from "@/lib/ads";
import { trackDemoRequested } from "@/lib/analytics";

// Stored with the lead as proof of what was agreed to, so it has to stay
// word-for-word identical to the sentence rendered in the form below.
const CONSENT_TEXT =
  "I agree that OwnPBX may contact me regarding this enquiry. Please see our Privacy Policy for details on how my information is handled.";

const sharpInput =
  "w-full bg-transparent border-b-2 border-white/15 focus:border-white outline-none py-3 text-white placeholder:text-neutral-500 transition-colors";

export const BookDemo = () => {
  // `website` is a honeypot: hidden from people, filled in by spam bots.
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", message: "", website: "" });
  const [teamSize, setTeamSize] = useState("");
  const [interest, setInterest] = useState("");
  const [date, setDate] = useState(null);
  const [consent, setConsent] = useState(false);
  // Promotional messages are a separate, optional opt-in and start unchecked.
  const [marketing, setMarketing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.company.trim() || !form.phone.trim()) {
      toast.error("Please add your name, company, work email and phone number.");
      return;
    }
    if (!consent) {
      toast.error("Please accept the Privacy Policy to continue.");
      return;
    }
    setLoading(true);
    try {
      await createLead({
        ...form,
        team_size: teamSize,
        interest,
        preferred_date: date ? format(date, "yyyy-MM-dd") : "",
        page_url: window.location.href,
        // What they agreed to, in the words they saw, plus the campaign that
        // brought them here — a UTM link alone does not attribute a lead.
        consent_text: CONSENT_TEXT,
        marketing_consent: marketing,
        ...getAttribution(),
      });
      trackLeadCreated();
      trackDemoRequested("book-demo");
      setDone(true);
      toast.success("Demo requested — our team will reach out shortly.");
    } catch (err) {
      toast.error(err?.response?.data?.error || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="book" className="relative py-24 md:py-32 bg-[#0a0a0a] border-t border-white/10 scroll-mt-20 overflow-hidden" data-testid="book-section">
      {/* warm amber glow, same family used across the site's featured cards */}
      <div
        className="pointer-events-none absolute left-1/2 top-0 -translate-x-1/2 w-[1100px] h-[500px]"
        style={{
          background: "radial-gradient(ellipse 50% 50% at 50% 0%, rgba(255,150,40,0.2), rgba(255,150,40,0) 70%)",
          filter: "blur(40px)",
        }}
      />
      <div className="relative mx-auto max-w-[1400px] px-6 md:px-12 grid lg:grid-cols-2 gap-16 items-center">
        <Reveal>
          <p className="font-mono-t text-xs uppercase tracking-[0.25em] text-[#5B8CFF] mb-5">Book a demo</p>
          <h2 className="font-heading font-medium tracking-tight text-4xl md:text-5xl lg:text-6xl leading-[0.98] text-white">
            Ready to hire your first AI agent?
          </h2>
          <p className="mt-6 text-neutral-400 max-w-md leading-relaxed">
            See OwnPBX handle a live call — inbound and outbound, in English, Hindi and Hinglish. We'll tailor the demo to your business.
          </p>
          <div className="mt-8 font-mono-t text-xs text-neutral-500 space-y-2">
            <p>→ 15-minute live walkthrough</p>
            <p>→ No card required · start a free trial</p>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="bg-white/[0.03] border border-white/10 p-7 md:p-9 shadow-[0_20px_60px_-25px_rgba(0,0,0,0.5)]">
            {done ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                className="min-h-[420px] flex flex-col items-center justify-center text-center"
                data-testid="book-success"
              >
                <CheckCircle2 size={48} className="text-white" />
                <h3 className="mt-6 font-heading font-semibold text-2xl text-white">You're on the list.</h3>
                <p className="mt-3 text-neutral-400 max-w-xs text-sm">
                  Thanks {form.name.split(" ")[0]} — our team will reach out to schedule your OwnPBX demo.
                </p>
              </motion.div>
            ) : (
              <form onSubmit={submit} className="space-y-6" data-testid="book-form">
                {/* Honeypot: off-screen and skipped by keyboard and screen readers, so only bots fill it. */}
                <input
                  type="text" name="website" value={form.website} onChange={upd("website")}
                  tabIndex={-1} autoComplete="off" aria-hidden="true"
                  style={{ position: "absolute", left: "-10000px", width: 1, height: 1, opacity: 0 }}
                />
                <div className="grid sm:grid-cols-2 gap-6">
                  <input className={sharpInput} placeholder="Full name *" value={form.name} onChange={upd("name")} data-testid="book-name" />
                  <input className={sharpInput} type="email" placeholder="Work email *" value={form.email} onChange={upd("email")} data-testid="book-email" />
                </div>
                <div className="grid sm:grid-cols-2 gap-6">
                  <input className={sharpInput} placeholder="Company name *" value={form.company} onChange={upd("company")} data-testid="book-company" />
                  <input className={sharpInput} type="tel" placeholder="Phone number *" value={form.phone} onChange={upd("phone")} data-testid="book-phone" />
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label className="font-mono-t text-[10px] uppercase tracking-widest text-neutral-500">Company size</label>
                    <Select value={teamSize} onValueChange={setTeamSize}>
                      <SelectTrigger className="mt-2 rounded-none border-0 border-b-2 border-white/15 bg-transparent px-0 text-white focus:ring-0 focus:border-white" data-testid="book-teamsize">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent className="rounded-none">
                        {["1–10", "11–50", "51–200", "200+"].map((s) => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="font-mono-t text-[10px] uppercase tracking-widest text-neutral-500">Interested in</label>
                    <Select value={interest} onValueChange={setInterest}>
                      <SelectTrigger className="mt-2 rounded-none border-0 border-b-2 border-white/15 bg-transparent px-0 text-white focus:ring-0 focus:border-white" data-testid="book-interest">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent className="rounded-none">
                        {["AI Voice Agent", "Cloud PBX", "Both", "Reseller / white-label"].map((s) => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <label className="font-mono-t text-[10px] uppercase tracking-widest text-neutral-500">Preferred date</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button type="button" className={`${sharpInput} flex items-center justify-between text-left`} data-testid="book-date">
                        <span className={date ? "text-white" : "text-neutral-500"}>
                          {date ? format(date, "PPP") : "Pick a date"}
                        </span>
                        <CalendarDays size={16} className="text-neutral-500" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 rounded-none" align="start">
                      <Calendar mode="single" selected={date} onSelect={setDate} disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))} initialFocus />
                    </PopoverContent>
                  </Popover>
                </div>

                <textarea className={`${sharpInput} resize-none`} rows={2} placeholder="What do you need help with? (optional)" value={form.message} onChange={upd("message")} data-testid="book-message" />

                <div className="space-y-3">
                  <label className="flex items-start gap-2.5">
                    <input
                      type="checkbox"
                      checked={consent}
                      onChange={(e) => setConsent(e.target.checked)}
                      className="mt-0.5 shrink-0 accent-white"
                      data-testid="book-consent"
                    />
                    <span className="text-[11px] leading-relaxed text-neutral-500">
                      I agree that OwnPBX may contact me regarding this enquiry. Please see our{" "}
                      <Link to="/privacy" target="_blank" className="underline underline-offset-2 hover:text-white">Privacy Policy</Link>{" "}
                      for details on how my information is handled.
                    </span>
                  </label>
                  {/* Promotional consent is deliberately separate and optional:
                      bundling it into the line above would make neither a real choice. */}
                  <label className="flex items-start gap-2.5">
                    <input
                      type="checkbox"
                      checked={marketing}
                      onChange={(e) => setMarketing(e.target.checked)}
                      className="mt-0.5 shrink-0 accent-white"
                      data-testid="book-marketing-consent"
                    />
                    <span className="text-[11px] leading-relaxed text-neutral-500">
                      I would also like to receive occasional product news and offers from OwnPBX.{" "}
                      <span className="text-neutral-600">(Optional)</span>
                    </span>
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="group w-full inline-flex items-center justify-center gap-2 rounded-full bg-white text-neutral-900 font-bold py-4 hover:bg-neutral-200 transition-colors duration-300 disabled:opacity-60"
                  data-testid="book-submit"
                >
                  {loading ? <Loader2 size={18} className="animate-spin" /> : <>Request my demo <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" /></>}
                </button>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
